import axios from "axios";
import fileDownload from "js-file-download";
import { getLanguage } from "../utils/LocaleUtil";
import { trackProjectCreated } from "../utils/analytics.utils";
import { CreateMockupForm, ProjectWithDesigns, Response } from "../types";

const USER_API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const BASE_ROUTE = "/api/v1/projects";

class ProjectService {
  static async getAllProjects() {
    return axios.get(
      `${USER_API_BASE_URL}${BASE_ROUTE}/`,
      this.getAuthHeader()
    );
  }

  static async createProject(data: { baseName: string; teamId: string }) {
    const res = await axios.post(
      `${USER_API_BASE_URL}${BASE_ROUTE}/project`,
      data,
      this.getAuthHeader()
    );

    const {
      data: {
        data: { id },
        status,
      },
    } = res;

    if (status === "success" && id) {
      trackProjectCreated();
    }

    return res;
  }

  static async moveProject(projectId: string, teamId: string) {
    return axios.post(
      `${USER_API_BASE_URL}${BASE_ROUTE}/project/${projectId}/moveToTeam/${teamId}`,
      undefined,
      this.getAuthHeader()
    );
  }

  static async deleteProject(projectId: string) {
    return axios.delete(
      `${USER_API_BASE_URL}${BASE_ROUTE}/project/${projectId}`,
      this.getAuthHeader()
    );
  }

  static async updateProjectName(data: { id: string; name: string }) {
    return axios.put(
      `${USER_API_BASE_URL}${BASE_ROUTE}/project/name`,
      data,
      this.getAuthHeader()
    );
  }

  static async getCollectSizeInfo(token: string) {
    return axios.post(
      `${USER_API_BASE_URL}${BASE_ROUTE}/collectSizeInfo`,
      { token },
      this.getAuthHeader()
    );
  }

  static async updateIcon(data: {
    uuid: string;
    assetVersionId: string;
    projectId: string;
  }) {
    return axios.put(
      `${USER_API_BASE_URL}${BASE_ROUTE}/project/icon`,
      data,
      this.getAuthHeader()
    );
  }

  static async downloadLeadTemplate(
    projectId: string,
    projectName: string,
    language: string
  ) {
    const resS3Url = await axios.post(
      `${USER_API_BASE_URL}${BASE_ROUTE}/project/${projectId}/generateSalesPresentation/${language}`,
      undefined,
      this.getAuthHeader()
    );

    const res = await axios.get(resS3Url.data.data, {
      responseType: "blob",
    });
    fileDownload(res.data, `${projectName}.pdf`);

    return res;
  }

  static async turnIntoTeam(projectId: string) {
    return axios.post(
      `${USER_API_BASE_URL}${BASE_ROUTE}/project/${projectId}/turnIntoTeam`,
      undefined,
      this.getAuthHeader()
    );
  }

  static async duplicate(projectId: string) {
    return axios.post(
      `${USER_API_BASE_URL}${BASE_ROUTE}/project/${projectId}/duplicate`,
      undefined,
      this.getAuthHeader()
    );
  }
  static async createMockup(formValues: CreateMockupForm) {
    const resS3Url = await axios.post(
      `${USER_API_BASE_URL}${BASE_ROUTE}/generateMultipleMockupPdf`,
      { ...formValues },
      this.getAuthHeader()
    );
    const res = await axios.get(resS3Url.data.data, {
      responseType: "blob",
    });
    fileDownload(res.data, `${formValues.projectName}.pdf`);
  }

  static async getProject(projectId: string) {
    return axios.get<Response<ProjectWithDesigns>>(
      `${USER_API_BASE_URL}${BASE_ROUTE}/project/${projectId}`,
      this.getAuthHeader()
    );
  }

  static async getProjectWithDesigns(projectId: string) {
    return axios.get<Response<ProjectWithDesigns>>(
      `${USER_API_BASE_URL}${BASE_ROUTE}/project/${projectId}/designs`,
      this.getAuthHeader()
    );
  }

  static getAuthHeader() {
    return {
      headers: {
        lang: getLanguage(),
      },
      withCredentials: true,
    };
  }
}

export default ProjectService;
